import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import Paths from './AppPaths';
import Modals from './App.modals';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Route, Redirect } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import {
  BrandContext,
  getActiveSourceForUser,
  useLaunchDarkly
} from '@shoflo/polymer';
import { shouldHaveFeature } from '@studio/feature.flags';
import { MetricsProvider, ATTRIBUTE_PERSONA_TYPE_HOST } from '@studio/metrics';
import {
  getEnvironmentType,
  broadcastTypesMap,
  VENDOR_AGORA
} from '@studio/constants';
import { datadogRum } from '@datadog/browser-rum';
import { getCurrentBroadcast, getSources } from '@shoflo/studio-redux/actions';
import { useRequireAuth, useLDPayload } from '@shoflo/studio-redux/hooks';
import { getTeam } from '@shoflo/studio-redux/actions/team';
import { getEvent } from '@shoflo/studio-redux/actions/event';
import { useTokenValidationOnFocus } from '@shoflo/studio-redux/utils';
import { getEnvVar } from '@studio/environment';
import { Loading } from '@shoflo/polymer';
import { getURLPathParams } from '@studio/logger';
import packageJSON from '../package.json';

const { version } = packageJSON;

const ChimeBroadcastPage = lazy(() =>
  import('containers/pages/Broadcast/Broadcast')
);

const ChimeJoin = lazy(() => import('./containers/Join/Join'));

const AgoraBroadcastPage = lazy(() =>
  import('./containers/pages/Broadcast/AgoraBroadcast')
);
const AgoraJoin = lazy(() => import('./containers/Join/Agora/AgoraJoin'));

const ScenePreview = lazy(() =>
  import('containers/pages/ScenePreview/ScenePreview')
);
const ScenePreviewV2 = lazy(() =>
  import('containers/pages/ScenePreview/ScenePreviewV2')
);

const App = () => {
  const user = useSelector(state => state.user, shallowEqual);
  const broadcast = useSelector(state => state.broadcast, shallowEqual);
  const brandingSettings = useSelector(
    state => state.brandingSettings,
    shallowEqual
  );
  const accountMappingId = useSelector(state => state.team?.externalId);
  const externalEventId = useSelector(state => state.event?.externalId);
  const eventTitle = useSelector(state => state.event?.title);
  const eventStartDate = useSelector(state => state.event?.startOn);
  const eventEndDate = useSelector(state => state.event?.endOn);

  const userId = user?._id;
  const externalUserId = user?.externalId;

  const sourceId = useSelector(state => {
    const broadcastSources = state.broadcastSources;
    const activeSource = broadcastSources;
    getActiveSourceForUser(broadcastSources, userId);
    return activeSource?._id;
  });
  const hasAttendeeCheckedReadiness = useSelector(
    state => state.attendeeReadinessCheck?.hasAttendeeCheckedReadiness,
    shallowEqual
  );
  useRequireAuth();

  const dispatch = useDispatch();

  let BroadcastPage = ChimeBroadcastPage;
  let Join = ChimeJoin;

  const meetingType = shouldHaveFeature('studio.meetingVendor');

  const isAgoraMeeting = meetingType === VENDOR_AGORA;
  if (isAgoraMeeting) {
    BroadcastPage = AgoraBroadcastPage;
    Join = AgoraJoin;
  }

  useEffect(() => {
    dispatch(getTeam());
    dispatch(getEvent());
    dispatch(getCurrentBroadcast());
    dispatch(getSources());
  }, [dispatch]);

  const pathParams = getURLPathParams();

  const teamId = pathParams?.teamId;
  const eventId = pathParams?.eventId;
  const broadcastId = pathParams?.broadcastId;

  const joinRoute = `/studio-director/teams/${teamId}/events/${eventId}/broadcasts/${broadcastId}/join`;

  const metricProps = useMemo(
    () => ({
      accountMappingId,
      attendeeId: externalUserId,
      studioUserId: userId,
      persona: ATTRIBUTE_PERSONA_TYPE_HOST,
      rudderstackWriteKey: getEnvVar('REACT_APP_RUDDERSTACK_WRITE_KEY'),
      rudderstackDataPlaneUrl: getEnvVar(
        'REACT_APP_RUDDERSTACK_DATA_PLANE_URL'
      ),
      environmentName: getEnvironmentType(getEnvVar('REACT_APP_CVENT_ENV')),
      appVersion: version,
      broadcastId: broadcast?._id,
      teamId: broadcast?.team,
      studioEventId: broadcast?.event,
      sourceId,
      sessionId: broadcast?.externalId,
      sessionStartDate: new Date(broadcast?.startOn),
      sessionEndDate: new Date(broadcast?.endOn),
      sessionName: broadcast?.title,
      broadcastType: broadcastTypesMap.get(
        broadcast?.meta?.broadcastType?.toLowerCase()
      ),
      eventId: externalEventId,
      eventName: eventTitle,
      eventStartDate: new Date(eventStartDate),
      eventEndDate: new Date(eventEndDate),
      meetingType
    }),
    [
      accountMappingId,
      broadcast?._id,
      broadcast?.endOn,
      broadcast?.event,
      broadcast?.externalId,
      broadcast?.meta?.broadcastType,
      broadcast?.startOn,
      broadcast?.team,
      broadcast?.title,
      externalUserId,
      sourceId,
      userId,
      externalEventId,
      eventTitle,
      eventStartDate,
      eventEndDate,
      meetingType
    ]
  );

  const ldKey = getEnvVar('REACT_APP_LAUNCHDARKLY_KEY');
  const ldPayload = useLDPayload();

  const isLDInitializedOrTimedOut = useLaunchDarkly(ldPayload, ldKey);

  if (!isLDInitializedOrTimedOut) return <></>;
  const isNewScenePreview = shouldHaveFeature('studio.scenePreviewAdjustments');
  const ScenePreviewComponent = isNewScenePreview
    ? ScenePreviewV2
    : ScenePreview;

  return (
    <>
      <AppEffects />
      <MetricsProvider {...metricProps}>
        <BrandContext.Provider value={brandingSettings}>
          <Modals />
          <Suspense fallback={<Loading />}>
            <Route exact path={Paths.BroadcastPage}>
              {hasAttendeeCheckedReadiness ? (
                <BroadcastPage />
              ) : (
                <Redirect to={joinRoute} />
              )}
            </Route>
            <Route
              exact
              path={Paths.ScenePreview}
              component={ScenePreviewComponent}
            />
            <Route exact path={Paths.JoinPage} component={Join} />
          </Suspense>
        </BrandContext.Provider>
      </MetricsProvider>
    </>
  );
};

/**
 * Encapsulate Application level side effects as to not cause the rest of the
 * app to re-render due to useEffects and redux selectors
 */
function AppEffects() {
  const userId = useSelector(state => state.user?._id);
  const externalUserId = useSelector(state => state.user?.externalId);
  const accountMappingId = useSelector(state => state.team?.externalId);

  useEffect(() => {
    if (userId) {
      datadogRum.setUser({
        id: userId
      });

      datadogRum.setGlobalContextProperty('studio.user.id', userId);
      datadogLogs.setGlobalContextProperty('studio.user.id', userId);
    }
  }, [userId]);

  useEffect(() => {
    if (externalUserId) {
      datadogRum.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
      datadogLogs.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
    }
  }, [externalUserId]);

  useEffect(() => {
    if (accountMappingId) {
      datadogRum.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
      datadogLogs.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
    }
  }, [accountMappingId]);

  /**
   *  Event listener for tab focus: We use this to confirm the user is still
   * authenticated and authorised when the tab becomes focused
   */
  useTokenValidationOnFocus(userId != null);

  return null;
}

export default App;
